/**
 * Audio/video preferences for the onboarding agent (FLO-51).
 *
 * Replaces the old `floe_text_mode` flag, which conflated three separate facts
 * into one sticky boolean: "the mic is off", "show me a text box", and
 * "generate no TTS". That coupling is the bug this module exists to remove —
 * a user who typed an email address got a silent agent and no way to understand
 * why. There is no text-vs-voice MODE any more; there are two independent
 * preferences with two independent controls:
 *
 *   mic     — do I want to talk? (the mic button)
 *   speaker — do I want to be talked to? (the speaker button)
 *
 * Neither implies the other, and the text composer is always available
 * regardless of both — the website agent's model (FLO-320/349).
 *
 * Kept as pure functions over an injected storage so the migration and the
 * fail-open behaviour are unit-testable without a DOM.
 */

/** Mic preference: 'on' | 'off'. Absent → the caller's default. */
export const MIC_PREF_KEY = 'floe_mic_pref';
/** Speaker preference: 'on' | 'off'. Absent → the caller's default. */
export const SPEAKER_PREF_KEY = 'floe_speaker_pref';
/** Pre-FLO-51 coupled flag. Read once for migration, then removed. */
export const LEGACY_TEXT_MODE_KEY = 'floe_text_mode';

export interface AvPrefs {
  /** True when the visitor's microphone should be live. */
  micEnabled: boolean;
  /** True when the bot's voice should be silenced (client AND server TTS). */
  speakerMuted: boolean;
}

/** The subset of the Storage interface this module needs. */
export interface PrefStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** `localStorage`, or null when it's unavailable (Safari private mode, SSR). */
function defaultStorage(): PrefStorage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

function readBool(storage: PrefStorage, key: string): boolean | undefined {
  try {
    const raw = storage.getItem(key);
    if (raw === 'on') return true;
    if (raw === 'off') return false;
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Resolve the stored preferences, applying the legacy migration.
 *
 * Migration rule — deliberately asymmetric. `floe_text_mode: 'true'` migrates to
 * `mic: off` and NOTHING ELSE. The old flag silenced the bot too, but it was
 * never a *speaker* preference: it became one by accident of the coupling. The
 * only intent it honestly recorded is "I want to type", which is `mic: off`.
 * Carrying it into `speaker: off` would invent consent the user never gave, and
 * would preserve exactly the "why did it stop talking?" confusion FLO-51 removes.
 * Anyone who does want silence now has a one-click speaker button.
 *
 * An explicit `floe_mic_pref` always wins over the legacy flag — a user who has
 * set the new preference has already spoken more recently.
 */
export function resolveAvPrefs(storage: PrefStorage, defaults: AvPrefs): AvPrefs {
  const mic = readBool(storage, MIC_PREF_KEY);
  const speaker = readBool(storage, SPEAKER_PREF_KEY);

  let micEnabled = mic ?? defaults.micEnabled;
  if (mic === undefined) {
    try {
      if (storage.getItem(LEGACY_TEXT_MODE_KEY) === 'true') micEnabled = false;
    } catch {
      /* storage unreadable — keep the default */
    }
  }

  return {
    micEnabled,
    // `speakerMuted` inverts the stored value: the key records "speaker on/off"
    // (what the button says) rather than "muted", so the storage reads the same
    // way the UI does.
    speakerMuted: speaker === undefined ? defaults.speakerMuted : !speaker,
  };
}

/**
 * Read the preferences and retire the legacy key in the same pass.
 *
 * The legacy key is removed only AFTER its value has been folded into the
 * result, so a storage that throws on write still yields a correctly-migrated
 * in-memory preference for this page load.
 */
export function readAvPrefs(
  defaults: AvPrefs,
  storage: PrefStorage | null = defaultStorage(),
): AvPrefs {
  if (!storage) return { ...defaults };
  const prefs = resolveAvPrefs(storage, defaults);
  try {
    const legacy = storage.getItem(LEGACY_TEXT_MODE_KEY);
    if (legacy !== null) {
      // Only 'true' carries an actual choice ("I want to type"), and only that
      // gets written forward. Any other legacy value — 'false', or junk — means
      // the user never expressed one, so the key is simply retired. Writing
      // `prefs.micEnabled` there would freeze today's CALLER DEFAULT into
      // storage as if the user had picked it, and a site that later flips
      // `enableAudio` would be silently overridden by that fossil.
      if (legacy === 'true' && readBool(storage, MIC_PREF_KEY) === undefined) {
        storage.setItem(MIC_PREF_KEY, 'off');
      }
      storage.removeItem(LEGACY_TEXT_MODE_KEY);
    }
  } catch {
    /* storage not writable — the in-memory value above is still correct */
  }
  return prefs;
}

/** Persist the mic preference. Silently no-ops when storage is unavailable. */
export function writeMicPref(
  enabled: boolean,
  storage: PrefStorage | null = defaultStorage(),
): void {
  try {
    storage?.setItem(MIC_PREF_KEY, enabled ? 'on' : 'off');
  } catch {
    /* localStorage not available */
  }
}

/** Persist the speaker preference. Silently no-ops when storage is unavailable. */
export function writeSpeakerPref(
  muted: boolean,
  storage: PrefStorage | null = defaultStorage(),
): void {
  try {
    storage?.setItem(SPEAKER_PREF_KEY, muted ? 'off' : 'on');
  } catch {
    /* localStorage not available */
  }
}
